import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { extractErrorMessage } from '../api/errors';
import type { SurveyAnswers } from '../api/intake';
import {
  answerAtStation,
  identifyAtStation,
  openStation,
  type Station,
  type StationRecording,
} from '../api/postSurvey';
import { SurveyForm, cleanSurveyAnswers, validateSurvey } from '../components/intake/SurveyForm';

function recordingLabel(r: StationRecording): string {
  const when = r.started_at
    ? new Date(r.started_at).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Unknown time';
  const mins = r.duration_s ? ` · ${Math.max(1, Math.round(r.duration_s / 60))} min` : '';
  return `${r.procedure_name ?? 'Recording'} — ${when}${mins}`;
}

/**
 * The sim post survey (POST-SURVEYS), opened from the QR code at the
 * simulation station. Public. A phone that enrolled at the booth is already
 * linked to its participant (an HttpOnly cookie the server set); otherwise
 * they identify with the email or badge code they enrolled with. They pick
 * which of their recent recordings the answers are about, then answer.
 */
export function PostSurveyPage() {
  const { code = '' } = useParams();
  const [station, setStation] = useState<Station | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [picked, setPicked] = useState('');
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [showErrors, setShowErrors] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    openStation(code)
      .then((s) => !cancelled && setStation(s))
      .catch((e: unknown) => {
        if (!cancelled) setError(extractErrorMessage(e, 'this survey link can’t be used'));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Survey
        </Typography>
        <Alert severity="info">{error}</Alert>
      </Container>
    );
  }
  if (!station) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }

  const def = station.form.definition;
  const identify = async () => {
    const value = who.trim();
    if (!value) return;
    setBusy(true);
    setProblem(null);
    try {
      setStation(
        await identifyAtStation(code, value.includes('@') ? { email: value } : { badge: value }),
      );
    } catch (e) {
      setProblem(extractErrorMessage(e, 'we couldn’t find you'));
    } finally {
      setBusy(false);
    }
  };

  const errors = validateSurvey(def, answers);
  const submit = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    if (station.recordings.length > 0 && !picked) {
      setProblem('Pick the recording you’re answering about.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await answerAtStation(code, {
        form_id: station.form.id,
        session_row_id: picked || null,
        answers: cleanSurveyAnswers(def, answers),
      });
      setDone(true);
    } catch (e) {
      setProblem(extractErrorMessage(e, 'your answers couldn’t be saved — try again'));
    } finally {
      setBusy(false);
    }
  };

  const another = async () => {
    setDone(false);
    setPicked('');
    setAnswers({});
    setShowErrors(false);
    try {
      setStation(await openStation(code));
    } catch {
      /* keep what we have */
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            {station.organisation}
          </Typography>
          <Typography variant="h5" component="h1">
            {def.title}
          </Typography>
        </Box>

        {done ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Thank you{station.participant_first_name ? `, ${station.participant_first_name}` : ''}!</Typography>
              <Typography>Your answers are saved.</Typography>
              {station.recordings.length > 1 && (
                <Box>
                  <Button variant="outlined" onClick={() => void another()}>
                    Answer for another recording
                  </Button>
                </Box>
              )}
            </Stack>
          </Paper>
        ) : station.participant_first_name === null ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography>
                Let us know who you are: the email or the badge code you enrolled with.
              </Typography>
              <TextField
                label="Email or badge code"
                value={who}
                onChange={(e) => setWho(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void identify()}
                autoComplete="email"
                fullWidth
              />
              {problem && <Alert severity="warning">{problem}</Alert>}
              <Box>
                <Button variant="contained" onClick={() => void identify()} disabled={busy || !who.trim()}>
                  Continue
                </Button>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <>
            <Typography>Hi {station.participant_first_name}.</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Which recording is this about?
              </Typography>
              {station.recordings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  We don’t see a recording of yours from the last two weeks — you can still
                  answer.
                </Typography>
              ) : (
                <RadioGroup value={picked} onChange={(e) => setPicked(e.target.value)}>
                  {station.recordings.map((r) => (
                    <FormControlLabel
                      key={r.id}
                      value={r.id}
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <span>{recordingLabel(r)}</span>
                          {r.answered && <Chip size="small" label="Answered" />}
                        </Stack>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            </Paper>
            <SurveyForm
              definition={def}
              answers={answers}
              onChange={setAnswers}
              errors={showErrors ? errors : {}}
              showHeader={false}
              disabled={busy}
            />
            {problem && <Alert severity="warning">{problem}</Alert>}
            <Box>
              <Button variant="contained" size="large" onClick={() => void submit()} disabled={busy}>
                {busy ? 'Saving…' : 'Send answers'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Container>
  );
}
